/**
 * useGeolocation
 * Layer: shared/hooks (no external deps beyond React)
 * Change: manejo-de-coordenadas-y-mapas
 *
 * Captures GPS coordinates once on mount via navigator.geolocation.getCurrentPosition.
 * Never throws — denial or timeout silently sets coordinates to null.
 * Callers use `coordinates ?? { lat: 0, lng: 0 }` as fallback.
 *
 * Exposes `retry()` to re-request coordinates on demand (e.g. a "location
 * disabled" badge the user can tap) — only re-opens the browser's native
 * permission prompt when the permission is still in 'prompt' state; once
 * explicitly denied, the browser blocks re-prompting entirely and this just
 * fails again the same way.
 */
import { useState, useEffect, useCallback } from 'react';

export interface GeolocationCoords {
  lat: number;
  lng: number;
}

export interface GeolocationState {
  /** null = denied / timeout / not yet captured */
  coordinates: GeolocationCoords | null;
  loading: boolean;
  error: GeolocationPositionError | null;
}

export interface UseGeolocationResult extends GeolocationState {
  /** Re-requests coordinates. Re-opens the native permission prompt only when not yet denied. */
  retry: () => void;
}

export function useGeolocation(timeoutMs = 5000): UseGeolocationResult {
  const [state, setState] = useState<GeolocationState>({
    coordinates: null,
    loading: true,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!navigator.geolocation) {
      setState({ coordinates: null, loading: false, error: null });
      return;
    }

    setState((s) => ({ ...s, loading: true }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({
          coordinates: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          loading: false,
          error: null,
        });
      },
      (err) => {
        setState({ coordinates: null, loading: false, error: err });
      },
      { timeout: timeoutMs },
    );
  }, [timeoutMs, attempt]);

  const retry = useCallback(() => { setAttempt((n) => n + 1); }, []);

  return { ...state, retry };
}
